import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IS_MOBILE } from '../constants';
import { useRerenderWithValue } from '../utils/common';

/* 
  onReorder(dest_position, src_position, dest_id, src_id) => Promise<boolean>
*/
var ctx = {};
const ReorderableList = ({ children:_children, className, onReorder, list_id}) => {
  list_id = list_id || "";
  /* enable dragstart after 3 seconds of touch  */
  const [children, setChildren] = useState([]);
  const [_render, rerender] = useRerenderWithValue();
  useEffect(
    () => {
      /* clean */
      if(ctx.prev_dragover) ctx.prev_dragover.style.paddingTop = "0";      
      if(ctx.draggable_el){
        ctx.draggable_el.removeAttribute("draggable");
        ctx.draggable_el.style.display = "block";
      }
      ctx.prev_dragover = null;
      ctx.draggable_el = null;
      setChildren(React.Children.toArray(_children));
    }, 
    [_children, _render]
  );

  const setupDraggable = (e) => {
    ctx.draggable_el = e.target.closest("[__draggable__]");
    ctx.draggable_el?.setAttribute("draggable", true);
    ctx.draggable_el_height = ctx.draggable_el?.clientHeight;
  }

  const showPlaceholder = (e) => {
    /* add a placeholder */
    if(ctx.original_placeholder) ctx.original_placeholder.remove();
    const original_placeholder = document.createElement("hr");
    original_placeholder.style.border = "1px green dashed";
    ctx.draggable_el?.insertAdjacentElement("afterend", original_placeholder);
    ctx.original_placeholder = original_placeholder;
    // dummy placeholder for last element
    ctx.last_dummy_holder && (ctx.last_dummy_holder.style.display= "block");
  }

  /* set up the dragging element */
  const handleTouchStart = (e) => {
    e.persist();
    ctx.touch_start = e.target;
    /* check if clicked on draggable hodler */
    if(e.target.getAttribute("is_draggable")){
      setupDraggable(e);
      return;
    }
  };

  const handleTouchEnd = (e) => {
    /* cleanup */
    ctx.touch_start = null;
    if(ctx.timeout) ctx.timeout= clearTimeout(ctx.timeout);
    ctx.draggable_el && ctx.draggable_el.removeAttribute("draggable");
    ctx.draggable_el = null;
  }

  const handleDragStart = (e, position) => {
    e.dataTransfer.setData("text/plain", [position, list_id].join(","));
    setTimeout(
      () => {
        ctx.draggable_el
          && (ctx.draggable_el.style.display= "none");
        showPlaceholder(e);
      }, 0
    ); // hide original element
  };
  
  const handleDragOver = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    /* show space on top the the current dragable */
    let _parent = e.target.closest("[__draggable__]");
    if(ctx.prev_dragover) ctx.prev_dragover.style.paddingTop = "0";
    ctx.prev_dragover = _parent;
    if(!_parent) return;
    _parent.style.paddingTop =  ctx.draggable_el_height + "px";
  });
  
  const handleDrop = useCallback(async (e, to_pos) => {
    /* perform reorder */
    e.stopPropagation();
    let _parent = e.target.closest("[__draggable__]");
    if(_parent){
      const [from_position, from_list_id] = e.dataTransfer.getData("text/plain").split(",");
      children.splice(to_pos, 0, children.splice(from_position, 1)[0]);
      onReorder && onReorder(to_pos, from_position, list_id, from_list_id)
    }
    ctx.original_placeholder?.remove();
    ctx.last_dummy_holder && (ctx.last_dummy_holder.style.display= "none");
    rerender();
  });

  useEffect(
    () => {
      document.body.addEventListener("dragover", handleDragOver);
      document.body.addEventListener("drop", handleDrop);
      return () => {
        document.body.removeEventListener("dragover", handleDragOver);
        document.body.removeEventListener("drop", handleDrop);
      };
    },
    [handleDragOver, handleDrop, ctx.root_el]
  );


  const onElementInitialized = (el) => {
    ctx.root_el = el;
    IS_MOBILE &&
      el?.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  return (
    <div className={className} ref={(el) => onElementInitialized(el)}>
      {children.map((el, i) => (
        <div
          key={el.key || i}
          __draggable__={i}
          onDragStart={(e) => handleDragStart(e, i)}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, i)}
          onTouchStart={(e) => handleTouchStart(e)}
          onTouchEnd={handleTouchEnd}
          onMouseDown={(e) => handleTouchStart(e)}
          onMouseUp={handleTouchEnd}
        >
          {el}
        </div>
      ))}
      <div /* dummy element to hold the last position */
          key={"last_dummy_holder"}
          __draggable__={children.length}
          onDragStart={(e) => handleDragStart(e, children.length)}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, children.length)}
          onTouchStart={(e) => handleTouchStart(e)}
          onTouchEnd={handleTouchEnd}
          onMouseDown={(e) => handleTouchStart(e)}
          onMouseUp={handleTouchEnd}
          style={{display: "none"}}
          ref={(el) => ctx.last_dummy_holder = el}
      >
        <div style={{"height": "100px"}}></div>
      </div>
    </div>
  );
};

export default ReorderableList;
